import {FC,useMemo, ReactNode} from 'react';
import {
  API,
  Project,
  Team,
} from "@brg/api";
import {
  FlexBox,
  useIsMobile,
  useBreakpoint,
} from "@brg/ui";
import { useProfile } from "core/auth";
import styled, { ThemeProvider, css } from "styled-components";
import { IContentViewItem } from '../../../hooks/api/useContentView';
import StartingAdBlock from '../components/AdCard/StartingAdBlock';

import ProjectButtons from "../components/ProjectButtons";
import { LayoutConst } from 'core';
import { IPollInfo } from "../../../constants/IPollInfo";
import { ProjectStartGameButton } from "../components/ProjectStartGameButton";
import { SP_VOTE_COLLECTION_SIGN } from "../../VotesAdmin/constants";
import { useRouteMatch } from 'react-router-dom';
import { LinkTo } from 'shared/constants/linkTo';
import { AppTester } from 'supporting';
import VoteBlock from '../components/VoteBlock/VoteBlock';
import { useTheme } from 'styled-components';
import { bProfile } from '@brg/auth';
import { BookDelegate } from '../../../../_deziiign/books/core/book-delegate';

export enum VOTE_BLOCK_POSITION {
  top = 'top',
  longread = 'longread',
  bottom = 'bottom',
  afterContainer = 'afterContainer',
  beforeBottomActions = 'beforeBottomActions',
}

const LONGLIST_FORMATION_VOTE_POSITIONS: ReadonlySet<VOTE_BLOCK_POSITION> = new Set([
  VOTE_BLOCK_POSITION.afterContainer,
  VOTE_BLOCK_POSITION.beforeBottomActions,
]);

export const isLonglistFormationVotePosition = (position: VOTE_BLOCK_POSITION) =>
  LONGLIST_FORMATION_VOTE_POSITIONS.has(position);

const BlocksWrapper = styled(FlexBox)<{ iSBookMainPage?: boolean, isCollectionVote?: boolean }>`
  width: 100%;
  flex-direction: column;
  row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '24px'};
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.l}px) {
    display: none;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '16px'};
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '8px'};
  }
  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    row-gap: 0px;
    width: ${({ iSBookMainPage }) => iSBookMainPage ? '100%' : 'calc(100% - 20px)'};
    margin: ${({ iSBookMainPage }) => iSBookMainPage ? '0 auto' : '0 10px'};
  }
`

const queueBtnAreaStyles = css`
  @media (max-width: ${LayoutConst.screen.l}px) and (min-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    align-self: stretch;
    margin-top: 16px;

    > * {
      width: 100%;
    }
  }
`;

const PsBottomTabletWrap = styled(FlexBox)`
  @media (max-width: ${LayoutConst.screen.l}px) and (min-width: ${LayoutConst.screen.s}px) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    .buttons-wrapper {
      width: 100%;
      flex-shrink: 0;
    }
  }

  .queue-btn-wrapper {
    ${queueBtnAreaStyles}
  }
`;

const PsBottomWrap = styled(FlexBox)<{ isProjectPage?: boolean }>`
  ${({ isProjectPage }) => isProjectPage && css`
    @media (max-width: ${LayoutConst.screen.l}px) and (min-width: ${LayoutConst.screen.s}px) {
      flex-direction: column;
      align-items: flex-start;

      .buttons-wrapper {
        width: 100%;
        flex-shrink: 0;
      }
    }
  `};

  .queue-btn-wrapper {
    ${queueBtnAreaStyles}
  }
`

type PropsT = {
  project: any;
  channel: Team;
  onShouldReload?: (project: Project) => void;
  reloadProject?: (time: any) => void;
  isProjectPage?: boolean;
  isEmptyProjectTags?: boolean;
  orientation?: 'horizontal' | 'vertical';
  isPresentationTooltip?: boolean;
  isPresentation?: boolean;
  iSBookMainPage?: boolean;
  isOutsideTextPosition?: boolean;
  pollInfo?: IPollInfo;
  queueButton?: ReactNode | null;
  isOwner?: boolean;
  onBlockedStateChange?: (isBlocked: boolean, reasons: number[], otherReason: string) => void;
  adItem?: IContentViewItem | null;
  setShowVoteEndState?: () => void;
};

export const PSBottom: FC<PropsT> = ({
  project,
  channel,
  onShouldReload,
  reloadProject,
  isProjectPage,
  iSBookMainPage,
  pollInfo,
  isEmptyProjectTags,
  orientation,
  isPresentationTooltip,
  isPresentation,
 queueButton,
  isOutsideTextPosition,
  isOwner,
  onBlockedStateChange,
  adItem,
  setShowVoteEndState
}) => {
  const isMobile = useIsMobile();
  const is744 = useBreakpoint(LayoutConst.screen.s);
  const is743 = useBreakpoint(LayoutConst.screen.s - 1);
  const theme = useTheme();
  const is944 = useBreakpoint(940);
  const isCollectionVote = useMemo(() => (project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN)), [project])
  const profile = useProfile();
  const isPreview = !!useRouteMatch(LinkTo.preview);
  const isSuperPuper = AppTester.isSuperPuper();
  const isMediiia = AppTester.isMediiia();
  const isModer = useMemo(() => profile && bProfile.isModer(profile), [profile]);

  const viewType = project?.longread?.viewType || 'container';

  const isPaidBook = useMemo(() => {
    const bookOrProject = project?.book || project;
    return BookDelegate.isPaid(bookOrProject) && (project?.isBook || project?.book);
  }, [project]);

  const isBook = project?.isBook || project?.book;
  const isBookChapter = project?.book && !project?.isBook;
  const isLMS = project?.status === 11;
  const isDraft = project?.status === 0;
  const shouldShowAd = (isBook ? (!isPaidBook && isBookChapter) : true) && !(project as any)?.isAdDisabled && !isLMS && !isDraft;

  const adCatalog = adItem && shouldShowAd ? <StartingAdBlock adItem={adItem} vtype={viewType} projectId={project?.projectId} /> : null;

  const shouldShowQueueButton = useMemo(
    () => !isSuperPuper && (!isMobile || is744),
    [isSuperPuper, isMobile, is744],
  );

  if (!project) {
    return null;
  }



  if (isProjectPage && is944 && !is743) {
    return (
      <>
        <PsBottomTabletWrap align="flex-end" alignMobile='flex-end' directionMobile={isPresentationTooltip ? 'column' : 'row'}>
          <ProjectButtons isPresentation={isPresentation} isOutsideTextPosition={isOutsideTextPosition}
                          dropdownVerticalPos={isEmptyProjectTags ? 'top' : "bottom"} project={project}
                          orientation={orientation} dropdownPos={isPresentationTooltip ? "right" : "left"}
                          onShouldReload={onShouldReload} isProjectPage={isProjectPage}
                          isPresentationTooltip={isPresentationTooltip}/>
          <FlexBox width={'100%'} widthMobile={'100%'} direction={"column"} directionMobile={"column"}>
            <ThemeProvider theme={theme as any}>
              <BlocksWrapper>
                {adCatalog}
                {isCollectionVote && !isPreview && isModer && isSuperPuper &&
                <FlexBox width={'100%'} justify="flex-end"><FlexBox width={is744 ? '100%' : undefined}
                                                                    offsetRightMobile={is744 ? 0 : 16}><ProjectStartGameButton
                  replayOnly projectId={project?.projectId} pollInfo={pollInfo}/></FlexBox></FlexBox>}
              </BlocksWrapper>
              {!isSuperPuper && queueButton && (
                <FlexBox className='queue-btn-wrapper' width={'100%'} justify="flex-end">
                  {queueButton}
                </FlexBox>
              )}
            </ThemeProvider>
          </FlexBox>
        </PsBottomTabletWrap>
      </>
    )
  }

  return (
    <>
      <PsBottomWrap align="flex-end" alignMobile='flex-start' width={'100%'} className={'ps-bottom-wrap'} isProjectPage={isProjectPage}>
        {!is944 && <ProjectButtons isPresentation={isPresentation} isOutsideTextPosition={isOutsideTextPosition}
                                   dropdownVerticalPos={isEmptyProjectTags ? 'top' : "bottom"} orientation={orientation}
                                   dropdownPos={isPresentationTooltip ? "right" : "left"} project={project}
                                   onShouldReload={onShouldReload} isProjectPage={isProjectPage}
                                   isPresentationTooltip={isPresentationTooltip}/>}
        <FlexBox className='vote-approval-block' width={'100%'} widthMobile={'100%'} direction={"column"} directionMobile={"column"}>
          <ThemeProvider theme={theme as any}>
            <BlocksWrapper iSBookMainPage={iSBookMainPage} isCollectionVote={isCollectionVote || adItem}>
              {
                <FlexBox width='100%' offsetLeft={!isMobile ? 24 : 0}>
                  <VoteBlock setShowVoteEndState={setShowVoteEndState} project={project} position={VOTE_BLOCK_POSITION.top}/>
                </FlexBox>
              }
              {adCatalog}
              {(!isMobile || is744) && isModer && !isPreview && (isSuperPuper || isMediiia) &&
              <FlexBox 
                className='start-game-btn-wrapper' 
                width={'100%'} 
                justify="flex-end">
                  <FlexBox width={is744 ? '100%' : undefined} offsetRightMobile={is744 ? 0 : 16}>
                    <ProjectStartGameButton
                      replayOnly 
                      projectId={project?.projectId} 
                      pollInfo={pollInfo}
                    />
                  </FlexBox>
                </FlexBox>}
            </BlocksWrapper>
            {shouldShowQueueButton && queueButton && (
              <FlexBox className='queue-btn-wrapper' width={'100%'} justify="flex-end">
                {queueButton}
              </FlexBox>
            )}
          </ThemeProvider>
        </FlexBox>
      </PsBottomWrap>
    </>
  );
};
import {FC,useMemo, ReactNode} from 'react';
import {
  API,
  Project,
  Team,
} from "@brg/api";
import {
  FlexBox,
  useIsMobile,
  useBreakpoint,
} from "@brg/ui";
import { useProfile } from "core/auth";
import styled, { ThemeProvider, css } from "styled-components";
import { IContentViewItem } from '../../../hooks/api/useContentView';
import StartingAdBlock from '../components/AdCard/StartingAdBlock';

import ProjectButtons from "../components/ProjectButtons";
import { LayoutConst } from 'core';
import { IPollInfo } from "../../../constants/IPollInfo";
import { ProjectStartGameButton } from "../components/ProjectStartGameButton";
import { SP_VOTE_COLLECTION_SIGN } from "../../VotesAdmin/constants";
import { useRouteMatch } from 'react-router-dom';
import { LinkTo } from 'shared/constants/linkTo';
import { AppTester } from 'supporting';
import VoteBlock from '../components/VoteBlock/VoteBlock';
import { useTheme } from 'styled-components';
import { bProfile } from '@brg/auth';
import { BookDelegate } from '../../../../_deziiign/books/core/book-delegate';

export enum VOTE_BLOCK_POSITION {
  top = 'top',
  longread = 'longread',
  bottom = 'bottom',
  afterContainer = 'afterContainer',
  beforeBottomActions = 'beforeBottomActions',
}

const LONGLIST_FORMATION_VOTE_POSITIONS: ReadonlySet<VOTE_BLOCK_POSITION> = new Set([
  VOTE_BLOCK_POSITION.afterContainer,
  VOTE_BLOCK_POSITION.beforeBottomActions,
]);

export const isLonglistFormationVotePosition = (position: VOTE_BLOCK_POSITION) =>
  LONGLIST_FORMATION_VOTE_POSITIONS.has(position);

const BlocksWrapper = styled(FlexBox)<{ iSBookMainPage?: boolean, isCollectionVote?: boolean }>`
  width: 100%;
  flex-direction: column;
  row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '24px'};
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.l}px) {
    display: none;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '16px'};
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '8px'};
  }
  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    row-gap: 0px;
    width: ${({ iSBookMainPage }) => iSBookMainPage ? '100%' : 'calc(100% - 20px)'};
    margin: ${({ iSBookMainPage }) => iSBookMainPage ? '0 auto' : '0 10px'};
  }
`

const queueBtnAreaStyles = css`
  @media (max-width: ${LayoutConst.screen.l}px) and (min-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    align-self: stretch;
    margin-top: 16px;

    > * {
      width: 100%;
    }
  }
`;

const PsBottomTabletWrap = styled(FlexBox)`
  @media (max-width: ${LayoutConst.screen.l}px) and (min-width: ${LayoutConst.screen.s}px) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    .buttons-wrapper {
      width: 100%;
      flex-shrink: 0;
    }
  }

  .queue-btn-wrapper {
    ${queueBtnAreaStyles}
  }
`;

const PsBottomWrap = styled(FlexBox)<{ isProjectPage?: boolean }>`
  ${({ isProjectPage }) => isProjectPage && css`
    @media (max-width: ${LayoutConst.screen.l}px) and (min-width: ${LayoutConst.screen.s}px) {
      flex-direction: column;
      align-items: flex-start;

      .buttons-wrapper {
        width: 100%;
        flex-shrink: 0;
      }
    }
  `};

  .queue-btn-wrapper {
    ${queueBtnAreaStyles}
  }
`

type PropsT = {
  project: any;
  channel: Team;
  onShouldReload?: (project: Project) => void;
  reloadProject?: (time: any) => void;
  isProjectPage?: boolean;
  isEmptyProjectTags?: boolean;
  orientation?: 'horizontal' | 'vertical';
  isPresentationTooltip?: boolean;
  isPresentation?: boolean;
  iSBookMainPage?: boolean;
  isOutsideTextPosition?: boolean;
  pollInfo?: IPollInfo;
  queueButton?: ReactNode | null;
  isOwner?: boolean;
  onBlockedStateChange?: (isBlocked: boolean, reasons: number[], otherReason: string) => void;
  adItem?: IContentViewItem | null;
  setShowVoteEndState?: () => void;
};

export const PSBottom: FC<PropsT> = ({
  project,
  channel,
  onShouldReload,
  reloadProject,
  isProjectPage,
  iSBookMainPage,
  pollInfo,
  isEmptyProjectTags,
  orientation,
  isPresentationTooltip,
  isPresentation,
 queueButton,
  isOutsideTextPosition,
  isOwner,
  onBlockedStateChange,
  adItem,
  setShowVoteEndState
}) => {
  const isMobile = useIsMobile();
  const is744 = useBreakpoint(LayoutConst.screen.s);
  const is743 = useBreakpoint(LayoutConst.screen.s - 1);
  const theme = useTheme();
  const is944 = useBreakpoint(940);
  const isCollectionVote = useMemo(() => (project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN)), [project])
  const profile = useProfile();
  const isPreview = !!useRouteMatch(LinkTo.preview);
  const isSuperPuper = AppTester.isSuperPuper();
  const isMediiia = AppTester.isMediiia();
  const isModer = useMemo(() => profile && bProfile.isModer(profile), [profile]);

  const viewType = project?.longread?.viewType || 'container';

  const isPaidBook = useMemo(() => {
    const bookOrProject = project?.book || project;
    return BookDelegate.isPaid(bookOrProject) && (project?.isBook || project?.book);
  }, [project]);

  const isBook = project?.isBook || project?.book;
  const isBookChapter = project?.book && !project?.isBook;
  const isLMS = project?.status === 11;
  const isDraft = project?.status === 0;
  const shouldShowAd = (isBook ? (!isPaidBook && isBookChapter) : true) && !(project as any)?.isAdDisabled && !isLMS && !isDraft;

  const adCatalog = adItem && shouldShowAd ? <StartingAdBlock adItem={adItem} vtype={viewType} projectId={project?.projectId} /> : null;

  const shouldShowQueueButton = useMemo(
    () => !isSuperPuper && (!isMobile || is744),
    [isSuperPuper, isMobile, is744],
  );

  if (!project) {
    return null;
  }



  if (isProjectPage && is944 && !is743) {
    return (
      <>
        <PsBottomTabletWrap align="flex-end" alignMobile='flex-end' directionMobile={isPresentationTooltip ? 'column' : 'row'}>
          <ProjectButtons isPresentation={isPresentation} isOutsideTextPosition={isOutsideTextPosition}
                          dropdownVerticalPos={isEmptyProjectTags ? 'top' : "bottom"} project={project}
                          orientation={orientation} dropdownPos={isPresentationTooltip ? "right" : "left"}
                          onShouldReload={onShouldReload} isProjectPage={isProjectPage}
                          isPresentationTooltip={isPresentationTooltip}/>
          <FlexBox width={'100%'} widthMobile={'100%'} direction={"column"} directionMobile={"column"}>
            <ThemeProvider theme={theme as any}>
              <BlocksWrapper>
                {adCatalog}
                {isCollectionVote && !isPreview && isModer && isSuperPuper &&
                <FlexBox width={'100%'} justify="flex-end"><FlexBox width={is744 ? '100%' : undefined}
                                                                    offsetRightMobile={is744 ? 0 : 16}><ProjectStartGameButton
                  replayOnly projectId={project?.projectId} pollInfo={pollInfo}/></FlexBox></FlexBox>}
              </BlocksWrapper>
              {!isSuperPuper && queueButton && (
                <FlexBox className='queue-btn-wrapper' width={'100%'} justify="flex-end">
                  {queueButton}
                </FlexBox>
              )}
            </ThemeProvider>
          </FlexBox>
        </PsBottomTabletWrap>
      </>
    )
  }

  return (
    <>
      <PsBottomWrap align="flex-end" alignMobile='flex-start' width={'100%'} className={'ps-bottom-wrap'} isProjectPage={isProjectPage}>
        {!is944 && <ProjectButtons isPresentation={isPresentation} isOutsideTextPosition={isOutsideTextPosition}
                                   dropdownVerticalPos={isEmptyProjectTags ? 'top' : "bottom"} orientation={orientation}
                                   dropdownPos={isPresentationTooltip ? "right" : "left"} project={project}
                                   onShouldReload={onShouldReload} isProjectPage={isProjectPage}
                                   isPresentationTooltip={isPresentationTooltip}/>}
        <FlexBox className='vote-approval-block' width={'100%'} widthMobile={'100%'} direction={"column"} directionMobile={"column"}>
          <ThemeProvider theme={theme as any}>
            <BlocksWrapper iSBookMainPage={iSBookMainPage} isCollectionVote={isCollectionVote || adItem}>
              {
                <FlexBox width='100%' offsetLeft={!isMobile ? 24 : 0}>
                  <VoteBlock setShowVoteEndState={setShowVoteEndState} project={project} position={VOTE_BLOCK_POSITION.top}/>
                </FlexBox>
              }
              {adCatalog}
              {(!isMobile || is744) && isModer && !isPreview && (isSuperPuper || isMediiia) &&
              <FlexBox 
                className='start-game-btn-wrapper' 
                width={'100%'} 
                justify="flex-end">
                  <FlexBox width={is744 ? '100%' : undefined} offsetRightMobile={is744 ? 0 : 16}>
                    <ProjectStartGameButton
                      replayOnly 
                      projectId={project?.projectId} 
                      pollInfo={pollInfo}
                    />
                  </FlexBox>
                </FlexBox>}
            </BlocksWrapper>
            {shouldShowQueueButton && queueButton && (
              <FlexBox className='queue-btn-wrapper' width={'100%'} justify="flex-end">
                {queueButton}
              </FlexBox>
            )}
          </ThemeProvider>
        </FlexBox>
      </PsBottomWrap>
    </>
  );
};
